import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import IconButton from "@mui/material/IconButton";
import { useDashboardTheme } from "../../../context/DashboardThemeContext";
import { useAlert } from "../../../context/AlertContext";

function formatMoney(value) {
  const number = Number(value || 0);
  return number.toLocaleString("es-EC", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatMoney6(value) {
  const number = Number(value || 0);
  return number.toLocaleString("es-EC", {
    minimumFractionDigits: 6,
    maximumFractionDigits: 6,
  });
}

function round6(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 1000000) / 1000000;
}

function normalizeIva(value) {
  const iva = Number(value || 0);
  if (iva === 999) return 999;
  if ([0, 5, 15].includes(iva)) return iva;
  return 0;
}

function getIvaLabel(value) {
  if (Number(value) === 999) return "No objeto";
  return `${Number(value || 0)}%`;
}

function getProductFinalPrice(product) {
  return Number(product?.precio_final ?? product?.pvp1 ?? product?.price ?? product?.precio ?? 0);
}

function getProductIva(product) {
  return normalizeIva(product?.iva_porcentaje ?? product?.iva ?? product?.iva_percent ?? 0);
}

function getProductCode(product) {
  return product?.code || product?.codigo || product?.codigo_principal || "";
}

function getProductAuxCode(product) {
  return product?.auxiliary_code || product?.codigo_auxiliar || product?.aux_code || "";
}

function getProductName(product) {
  return product?.name || product?.nombre || "";
}

function calculateLineFromFinalPrice(precioConIva, ivaPorcentaje, cantidad) {
  const finalUnitario = Number(precioConIva || 0);
  const qty = Number(cantidad || 0);
  const iva = normalizeIva(ivaPorcentaje);
  const totalFinal = round6(finalUnitario * qty);
  if (iva === 999 || iva <= 0) {
    return {
      precioSinIva: round6(finalUnitario),
      precioConIva: round6(finalUnitario),
      subtotal: totalFinal,
      ivaValor: 0,
      total: totalFinal,
    };
  }
  const subtotalExacto = totalFinal / (1 + iva / 100);
  const subtotal = round6(subtotalExacto);
  const ivaValor = round6(totalFinal - subtotal);
  const precioSinIva = qty > 0 ? round6(subtotalExacto / qty) : 0;
  return {
    precioSinIva,
    precioConIva: round6(finalUnitario),
    subtotal,
    ivaValor,
    total: totalFinal,
  };
}

function buildDetailFromProduct(product, cantidad, descripcion) {
  const qty = round6(cantidad || 0);
  const iva = getProductIva(product);
  const precioFinal = getProductFinalPrice(product);
  const calculo = calculateLineFromFinalPrice(precioFinal, iva, qty);
  const name = getProductName(product);
  return {
    uid: `${product.id}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    producto_id: product.id,
    codigo_principal: getProductCode(product),
    codigo_auxiliar: getProductAuxCode(product),
    nombre: name,
    descripcion: String(descripcion || "").trim(),
    cantidad: qty,
    precio_unitario: calculo.precioSinIva,
    precio_sin_iva: calculo.precioSinIva,
    precio_con_iva: calculo.precioConIva,
    iva_porcentaje: iva,
    iva_valor: calculo.ivaValor,
    subtotal: calculo.subtotal,
    total: calculo.total,
    observacion: "",
  };
}

function fieldSx(dashboardTheme) {
  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      color: dashboardTheme.text_color,
      bgcolor: dashboardTheme.input_background || dashboardTheme.card_background,
      "& fieldset": {
        borderColor: dashboardTheme.border_color,
      },
      "&:hover fieldset": {
        borderColor: dashboardTheme.primary_color,
      },
      "&.Mui-focused fieldset": {
        borderColor: dashboardTheme.primary_color,
      },
    },
    "& .MuiInputLabel-root": {
      color: dashboardTheme.muted_text_color,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: dashboardTheme.primary_color,
    },
    "& .MuiSvgIcon-root": {
      color: dashboardTheme.text_color,
    },
    "& input": {
      color: dashboardTheme.text_color,
    },
    "& textarea": {
      color: dashboardTheme.text_color,
    },
  };
}

export default function ModalDetail({
  open,
  product,
  onClose,
  onAddDetail,
}) {
  const { dashboardTheme } = useDashboardTheme();
  const { showError } = useAlert();
  const [cantidad, setCantidad] = useState("1");
  const [descripcion, setDescripcion] = useState("");

  useEffect(() => {
    if (open && product) {
      setCantidad("1");
      setDescripcion("");
    }
  }, [open, product]);

  const productInfo = useMemo(() => {
    if (!product) return null;
    const precioFinal = getProductFinalPrice(product);
    const iva = getProductIva(product);
    const calculo = calculateLineFromFinalPrice(precioFinal, iva, 1);
    return {
      codigo: getProductCode(product),
      codigoAuxiliar: getProductAuxCode(product),
      nombre: getProductName(product),
      precioFinal,
      iva,
      precioSinIva: calculo.precioSinIva,
    };
  }, [product]);

  const handleAdd = () => {
    if (!product?.id) {
      showError("Selecciona un producto desde el buscador.");
      return;
    }
    const qty = Number(cantidad || 0);
    if (qty <= 0) {
      showError("La cantidad debe ser mayor a cero.");
      return;
    }
    onAddDetail?.(buildDetailFromProduct(product, qty, descripcion));
    onClose?.();
  };

  if (!productInfo) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          bgcolor: dashboardTheme.card_background,
          color: dashboardTheme.text_color,
          border: `1px solid ${dashboardTheme.border_color}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 1.7,
          borderBottom: `1px solid ${dashboardTheme.border_color}`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 700,
              color: dashboardTheme.primary_color,
            }}
          >
            Configurar producto
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: dashboardTheme.text_color,
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          px: { xs: 2, md: 2.5 },
          py: "20px !important",
        }}
      >
        <Box
          sx={{
            borderRadius: "14px",
            border: `1px solid ${dashboardTheme.border_color}`,
            bgcolor: dashboardTheme.card_background,
            p: { xs: 1.5, md: 2 },
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: 17,
              fontWeight: 700,
              color: dashboardTheme.text_color,
            }}
          >
            {productInfo.nombre || "-"}
          </Typography>
          <Typography
            sx={{
              fontSize: 13,
              color: dashboardTheme.muted_text_color,
              mt: 0.5,
            }}
          >
            Código: {productInfo.codigo || "-"} · Aux: {productInfo.codigoAuxiliar || "-"}
          </Typography>
          <Typography
            sx={{
              fontSize: 13,
              color: dashboardTheme.primary_color,
              fontWeight: 700,
              mt: 0.5,
            }}
          >
            PVP: ${formatMoney(productInfo.precioFinal)} · SRI: ${formatMoney6(productInfo.precioSinIva)} · IVA {getIvaLabel(productInfo.iva)}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "130px 1fr",
            },
            gap: 1.5,
          }}
        >
          <TextField
            size="small"
            label="Cantidad"
            type="number"
            value={cantidad}
            onChange={(event) => setCantidad(event.target.value)}
            inputProps={{ step: "any" }}
            sx={fieldSx(dashboardTheme)}
          />
          <TextField
            fullWidth
            size="small"
            label="Descripción del producto"
            value={descripcion}
            onChange={(event) => setDescripcion(event.target.value)}
            sx={fieldSx(dashboardTheme)}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 1.7,
          borderTop: `1px solid ${dashboardTheme.border_color}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            minHeight: 40,
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 700,
            color: dashboardTheme.button_color,
            borderColor: dashboardTheme.button_color,
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={<AddRoundedIcon />}
          onClick={handleAdd}
          sx={{
            minHeight: 40,
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 700,
            bgcolor: dashboardTheme.button_color,
            color: dashboardTheme.button_text_color,
            px: 2,
            boxShadow: "none",
            "&:hover": {
              bgcolor: dashboardTheme.button_hover_color,
              boxShadow: "none",
            },
          }}
        >
          Añadir
        </Button>
      </DialogActions>
    </Dialog>
  );
}